import React from 'react'
import { graphql, useFragment } from 'react-relay/hooks'
import { filter } from 'lodash'
import { FlexColumn, Text, Table as Tables } from 'elements'
import ReportStatusUnknown from './ReportStatusUnknown'
import ErrorBanner from '../../common/ErrorBanner'
import ConnectionReportStatus from './ConnectionReportStatus'
import MissingDataStatus from './MissingDataStatus'

const { Table, Head, Body, Row, HeadCell, Cell } = Tables.Simple

const MISSING_COL_RATIO = 0.5

function filterColumns(userFile, props) {
  if (!userFile?.meta?.columns) {
    return []
  }

  return filter(userFile.meta.columns, props)
}

function sortBySeverity(userFiles) {
  const props = { found: false, required: true }
  return userFiles.sort(
    (uf1, uf2) => filterColumns(uf2, props).length - filterColumns(uf1, props).length,
  )
}

function hasMissingRequiredColumns(sourceFiles) {
  return sourceFiles.some(
    (userFile) => filterColumns(userFile, { found: false, required: true }).length > 0,
  )
}

export default function ReportStatusDetails({ report }) {
  const data = useFragment(
    graphql`
      fragment ReportStatusDetails_report on Report {
        id
        errors
        periodStart
        periodEnd
        reportUserFiles {
          nodes {
            type
            userFile {
              id
              name
              meta
            }
          }
        }
      }
    `,
    report,
  )

  const sourceFiles = sortBySeverity(
    data.reportUserFiles.nodes
      .filter((node) => node.type === 'SOURCE')
      .map((node) => node.userFile),
  )

  let manyMissingCols = sourceFiles.some((file) => {
    const unfounds = filterColumns(file, { found: false, required: true })
    const requiredColumns = filterColumns(file, { required: true })

    if (requiredColumns.length === 0) {
      return false
    }

    return unfounds.length / requiredColumns.length > MISSING_COL_RATIO
  })

  // show missing columns info if we have it in the file meta
  if (hasMissingRequiredColumns(sourceFiles)) {
    return (
      <FlexColumn p="xxlarge">
        {manyMissingCols ? (
          <FlexColumn mb="xxlarge">
            <ErrorBanner message="You seem to be missing many columns. Did you mean to select a different type of upload?" />
          </FlexColumn>
        ) : null}
        {sourceFiles.map((file, idx) => {
          const unfounds = filterColumns(file, { found: false })

          if (!unfounds.length) {
            return null
          }

          return (
            <FlexColumn key={file.id} mb="xxlarge">
              <Text mb="medium">{file.name}</Text>
              <FlexColumn px="large">
                <Table>
                  <Head>
                    <Row>
                      <HeadCell>Column</HeadCell>
                      <HeadCell>Status</HeadCell>
                      <HeadCell>Required</HeadCell>
                    </Row>
                  </Head>
                  <Body>
                    {unfounds.map((col) => (
                      <Row key={col.name}>
                        <Cell>
                          <Text truncate title={col.name}>
                            {col.name}
                          </Text>
                        </Cell>
                        <Cell>Missing</Cell>
                        <Cell>
                          <Text color={col.required ? 'danger.0' : 'foreground.1'}>
                            {col.required ? 'Yes' : 'No'}
                          </Text>
                        </Cell>
                      </Row>
                    ))}
                  </Body>
                </Table>
              </FlexColumn>
            </FlexColumn>
          )
        })}
      </FlexColumn>
    )
  }

  if (data?.errors?.some((error) => error.code === 'response-error')) {
    return <ConnectionReportStatus />
  }

  if (data?.errors?.some((error) => error.code === 'missing-data')) {
    return (
      <MissingDataStatus
        errors={data.errors}
        periodStart={data.periodStart}
        periodEnd={data.periodEnd}
      />
    )
  }

  // generic error message
  return <ReportStatusUnknown />
}
